import {
  Banner,
  Box,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeCardElement, StripeError } from '@stripe/stripe-js';
import { config } from 'lib-shared-backend-config';
import { FormEvent, useCallback, useState } from 'react';

import { billingApi } from 'apis/billing';
import { useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import styles from './addPaymentMethodModal.module.scss';

/** A component that displays a modal for adding a new payment method. */
export const AddPaymentMethodModal = createAppModal(
  { type: 'settings:addPaymentMethod' },
  ({ isOpen, meta, hide }): JSX.Element => {
    const notify = useNotifications();
    const stripe = useStripe();
    const elements = useElements();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stripeError, setStripeError] = useState<StripeError | undefined>();
    const {
      user: { email: loggedInUserEmail },
    } = useCurrentUser();
    const isStripeInTestMode = config.sdk.stripe.startsWith('pk_test');
    const [updateCreditCardToken] = billingApi.useCreateCreditCardMutation();

    const handleConfirm = async (
      event?: FormEvent<HTMLFormElement> | undefined,
    ) => {
      event?.preventDefault();

      if (
        !event ||
        !stripe ||
        !elements ||
        !elements.getElement(CardElement) ||
        !loggedInUserEmail
      ) {
        return;
      }

      setIsSubmitting(true);

      const { error: createTokenError, token } = await stripe.createToken(
        elements.getElement(CardElement) as StripeCardElement,
        {
          name: loggedInUserEmail,
        },
      );

      if (createTokenError) {
        setStripeError(createTokenError);
      }

      if (token) {
        try {
          const { data } = await updateCreditCardToken({
            token: { token },
          }).unwrap();

          notify({
            title: 'Success',
            text: `Payment method successfully added`,
            icon: 'done',
            className: 'success',
            time: 3000,
            showDesktop: false,
          });

          meta?.resolve({ token, paymentId: data.external_key });
        } catch (error: any) {
          const errorMessage =
            error?.data?.errors?.[0] ?? `Please try again or contact support.`;

          notify({
            title: 'Failed to add payment method',
            text: errorMessage,
            icon: 'exclamation',
            className: 'danger',
            time: 3000,
            showDesktop: false,
          });
        }
      }

      setIsSubmitting(false);
      handleCancel();
    };

    const handleCancel = useCallback(() => {
      setStripeError(undefined);
      hide?.();
    }, [hide]);

    return (
      <Modal isOpen={isOpen} onClose={handleCancel} className={styles.modal}>
        <ModalHead title="Add new payment method" onClose={handleCancel} />

        <ModalBody>
          {stripeError && (
            <Box margin="0 0 300">
              <Banner
                variant="fail"
                description={
                  <Text.Paragraph>{stripeError.message}</Text.Paragraph>
                }
              />
            </Box>
          )}
          {isStripeInTestMode && (
            <Box padding="200 0">
              <Text.Paragraph subdued>
                Test card: 4000002500003155
              </Text.Paragraph>
            </Box>
          )}
          <form onSubmit={handleConfirm}>
            <CardElement
              className={styles.stripeCardElement}
              options={{
                disabled: isSubmitting,
              }}
            />
          </form>
        </ModalBody>

        <ModalFoot
          confirmOnClick={handleConfirm}
          cancelOnClick={handleCancel}
          confirmName="Add payment method"
          cancelName="Cancel"
          confirmProps={{
            disabled: false,
            isLoading: isSubmitting,
          }}
        />
      </Modal>
    );
  },
);
